import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Select,
  MenuItem,
  ListSubheader,
  Typography,
  Chip,
  Divider,
  TextField,
  InputAdornment,
  Checkbox,
} from '@mui/material';
import { Search as SearchIcon, WarningAmber as WarningAmberIcon, Close as CloseIcon } from '@mui/icons-material';

export interface FieldOption {
  id: string;
  name: string;
  required?: boolean;
  tooltip?: string;
  /** Optional sub-group heading (e.g. Origin/Destination/Transport) rendered within the group. */
  header?: string;
}

export interface FieldGroup {
  group: string;
  fields: FieldOption[];
}

interface FieldSelectorDropdownProps {
  columnIndex: number;
  columnName: string;
  options: FieldGroup[];
  currentMapping?: string;
  sampleData: string[];
  onSelect: (fieldId: string) => void;
  alreadyMappedFields?: string[];
  /** fieldId → original column name for mappings dropped because the column is missing */
  droppedMappings?: Record<string, string>;
  /** Enable multi-select for Transport Details fields */
  multiSelect?: boolean;
}

export const FieldSelectorDropdown: React.FC<FieldSelectorDropdownProps> = ({
  columnIndex,
  columnName,
  options,
  currentMapping,
  sampleData,
  onSelect,
  alreadyMappedFields = [],
  droppedMappings = {},
  multiSelect = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const keepOpenRef = useRef(false);
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);

  // Callback ref to attach ResizeObserver when the sticky header mounts
  const stickyHeaderCallbackRef = useCallback((node: HTMLDivElement | null) => {
    // Cleanup previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    stickyHeaderRef.current = node;

    if (node) {
      setStickyHeaderHeight(node.offsetHeight);
      observerRef.current = new ResizeObserver(() => {
        setStickyHeaderHeight(node.offsetHeight);
      });
      observerRef.current.observe(node);
    }
  }, []);

  // Parse selected fields for multi-select mode
  const selectedFields = useMemo(() => {
    if (!currentMapping) return [];
    return currentMapping.split('||').filter(Boolean);
  }, [currentMapping]);

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const findFieldById = (fieldId: string): FieldOption | undefined => {
    for (const group of options) {
      const field = group.fields.find((f) => f.id === fieldId);
      if (field) return field;
    }
    return undefined;
  };

  // Check if a group is a Transport Details group
  const isTransportGroup = useCallback((groupName: string): boolean => {
    return groupName.toLowerCase().includes('transport details');
  }, []);

  // Check if a field belongs to a Transport Details group
  const isTransportDetailField = useCallback((fieldId: string): boolean => {
    return options.some(group =>
      isTransportGroup(group.group) &&
      group.fields.some(f => f.id === fieldId)
    );
  }, [options, isTransportGroup]);

  const isFieldAlreadyMapped = (fieldId: string) => {
    if (multiSelect) {
      // In multi-select mode, don't disable fields that are selected in THIS column
      return alreadyMappedFields.includes(fieldId) && !selectedFields.includes(fieldId);
    }
    return alreadyMappedFields.includes(fieldId) && currentMapping !== fieldId;
  };

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;

    const lowerSearch = searchTerm.toLowerCase();
    return options
      .map((group) => ({
        ...group,
        fields: group.fields.filter((field) =>
          field.name.toLowerCase().includes(lowerSearch)
        ),
      }))
      .filter((group) => group.fields.length > 0);
  }, [options, searchTerm]);

  // Handle field toggle in multi-select mode
  const handleFieldToggle = useCallback((fieldId: string) => {
    if (!multiSelect) {
      onSelect(fieldId);
      setSearchTerm('');
      return;
    }

    const isTransport = isTransportDetailField(fieldId);

    if (isTransport) {
      // Toggle transport detail field
      let newSelected: string[];
      if (selectedFields.includes(fieldId)) {
        newSelected = selectedFields.filter(f => f !== fieldId);
      } else {
        newSelected = [...selectedFields, fieldId];
      }
      onSelect(newSelected.join('||'));
      // Dropdown stays open (keepOpenRef set via onMouseDown)
    } else {
      // Non-transport: single-select behavior — replace any non-transport selection, keep transport ones
      const transportOnly = selectedFields.filter(f => isTransportDetailField(f));
      if (fieldId) {
        // Remove any previous non-transport selection, add new one
        onSelect([fieldId, ...transportOnly].join('||'));
      } else {
        onSelect(transportOnly.join('||') || '');
      }
      setSearchTerm('');
      setOpen(false);
    }
  }, [multiSelect, selectedFields, onSelect, isTransportDetailField]);

  // Remove a specific field chip
  const handleRemoveField = useCallback((fieldId: string) => {
    const newSelected = selectedFields.filter(f => f !== fieldId);
    onSelect(newSelected.join('||') || '');
  }, [selectedFields, onSelect]);

  // Clear all mappings
  const handleClearAll = useCallback(() => {
    onSelect('');
    setSearchTerm('');
  }, [onSelect]);

  const handleSelectChange = (value: string) => {
    onSelect(value);
    setSearchTerm('');
  };

  const hasMapping = multiSelect ? selectedFields.length > 0 : !!currentMapping;

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      {/* Simple select that looks like Excel header */}
      <Select
        fullWidth
        size="small"
        {...(multiSelect ? {
          open,
          onOpen: () => setOpen(true),
          onClose: () => {
            if (keepOpenRef.current) {
              keepOpenRef.current = false;
              return;
            }
            setOpen(false);
            setSearchTerm('');
          },
          value: '',
          onChange: () => {},
        } : {
          value: currentMapping || '',
          onChange: (e: any) => handleSelectChange(e.target.value),
          onClose: () => setSearchTerm(''),
        })}
        displayEmpty
        renderValue={() => {
          if (multiSelect) {
            if (selectedFields.length === 0) {
              return (
                <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 700, fontStyle: 'italic', lineHeight: '1.25rem', color: 'text.secondary' }}>
                  Select field...
                </Typography>
              );
            }
            if (selectedFields.length === 1) {
              const field = findFieldById(selectedFields[0]);
              return (
                <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 500, lineHeight: '1.25rem' }}>
                  {field?.name || selectedFields[0]}
                </Typography>
              );
            }
            return (
              <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 500, lineHeight: '1.25rem' }}>
                {selectedFields.length} fields mapped
              </Typography>
            );
          }
          if (!currentMapping) {
            return (
              <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 700, fontStyle: 'italic', lineHeight: '1.25rem', color: 'text.secondary' }}>
                Select field...
              </Typography>
            );
          }
          const field = findFieldById(currentMapping);
          return (
            <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 500, lineHeight: '1.25rem' }}>
              {field?.name || currentMapping}
            </Typography>
          );
        }}
        sx={{
          bgcolor: hasMapping ? 'success.50' : 'white',
          '& .MuiSelect-select': { py: '5px !important', px: '8px !important', minHeight: '0 !important', lineHeight: '1.25rem', display: 'flex !important', alignItems: 'center' },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: hasMapping ? 'success.main' : 'grey.300',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: hasMapping ? 'success.dark' : 'primary.main',
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 400,
              width: 350,
              mt: 0.5,
            },
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        }}
      >
          {/* Search box */}
          <Box ref={stickyHeaderCallbackRef} sx={{ px: 1.5, pt: 1.5, pb: 0, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 2, boxShadow: '0 2px 4px -2px rgba(0,0,0,0.08)' }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search fields or column number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 0.75 }}
            />

            {/* Selected fields chips — compact section below search (multi-select only) */}
            {multiSelect && selectedFields.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mt: 0.5,
                  mb: 0,
                  p: 0.75,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.200',
                }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1 }}>
                  {selectedFields.map(fieldId => {
                    const field = findFieldById(fieldId);
                    return (
                      <Chip
                        key={fieldId}
                        label={field?.name || fieldId}
                        size="small"
                        color={isTransportDetailField(fieldId) ? 'primary' : 'default'}
                        variant="outlined"
                        onDelete={() => handleRemoveField(fieldId)}
                        deleteIcon={<CloseIcon sx={{ fontSize: '14px !important' }} />}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          keepOpenRef.current = true;
                        }}
                        sx={{
                          height: 22,
                          fontSize: '0.68rem',
                          '& .MuiChip-label': { px: 0.75 },
                          '& .MuiChip-deleteIcon': { mr: 0.25 },
                        }}
                      />
                    );
                  })}
                </Box>
                <CloseIcon
                  onClick={(e) => { e.stopPropagation(); handleClearAll(); }}
                  onMouseDown={(e) => { e.stopPropagation(); keepOpenRef.current = true; }}
                  sx={{
                    fontSize: 16,
                    color: 'error.main',
                    cursor: 'pointer',
                    flexShrink: 0,
                    borderRadius: '50%',
                    p: 0.25,
                    '&:hover': { bgcolor: 'error.50' },
                  }}
                />
              </Box>
            )}

            <Divider sx={{ mt: 0.75 }} />
          </Box>

          {/* Not mapped / Clear option (single-select only — multi-select uses clear button in chips) */}
          {!multiSelect && (
            <>
              <MenuItem
                value=""
                sx={{
                  fontStyle: 'normal',
                  fontWeight: 600,
                  color: hasMapping ? 'error.main' : 'text.secondary',
                  bgcolor: hasMapping ? 'error.50' : 'grey.50',
                  borderLeft: hasMapping ? '4px solid' : 'none',
                  borderLeftColor: 'error.main',
                  '&:hover': {
                    bgcolor: hasMapping ? 'error.100' : 'grey.100',
                  },
                  py: 1.5,
                }}
              >
                {hasMapping ? '✕ Clear Mapping' : '(Not Mapped)'}
              </MenuItem>
              <Divider sx={{ my: 1 }} />
            </>
          )}

          {/* Field groups */}
          {filteredOptions.length > 0 ? (
            filteredOptions.map((group, groupIdx) => {
              const isTransport = isTransportGroup(group.group);
              const legGroupMatch = /^(Leg \d+)(.{0,500})$/.exec(group.group);
              return [
                <ListSubheader
                  key={`header-${group.group}`}
                  sx={{
                    top: stickyHeaderHeight,
                    zIndex: 1,
                    bgcolor: isTransport ? 'primary.100' : 'primary.50',
                    color: 'primary.dark',
                    fontWeight: 'bold',
                    lineHeight: isTransport ? '36px' : '32px',
                    fontSize: isTransport ? '0.8rem' : undefined,
                    borderTop: isTransport ? '2px solid' : 'none',
                    borderTopColor: 'primary.main',
                  }}
                >
                  {isTransport && legGroupMatch ? (
                    <>
                      <Typography component="span" sx={{ fontWeight: 900, fontSize: '0.85rem' }}>
                        {legGroupMatch[1]}
                      </Typography>
                      {legGroupMatch[2]}
                    </>
                  ) : (
                    group.group
                  )}
                  {multiSelect && isTransport && (
                    <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.6, fontWeight: 400 }}>
                      (multi-select)
                    </Typography>
                  )}
                </ListSubheader>,
                ...group.fields.flatMap((field, fieldIdx) => {
                  const droppedFrom = droppedMappings[field.id];
                  const isSelected = multiSelect && selectedFields.includes(field.id);
                  const isMultiSelectable = multiSelect && isTransport;

                  // Sub-group heading (e.g. Origin/Destination/Transport), emitted once
                  // whenever the field's header changes within the group.
                  const prevHeader = fieldIdx > 0 ? group.fields[fieldIdx - 1].header : undefined;
                  const subHeader = field.header && field.header !== prevHeader ? (
                    <ListSubheader
                      key={`subheader-${group.group}-${field.header}`}
                      sx={{
                        position: 'static',
                        lineHeight: '26px',
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        letterSpacing: '0.03em',
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                        bgcolor: 'grey.50',
                        pl: 2,
                      }}
                    >
                      {field.header}
                    </ListSubheader>
                  ) : null;

                  const menuItem = (
                    <MenuItem
                      key={field.id}
                      value={field.id}
                      disabled={isFieldAlreadyMapped(field.id)}
                      {...(multiSelect ? {
                        onMouseDown: () => {
                          if (isMultiSelectable) {
                            keepOpenRef.current = true;
                          }
                        },
                        onClick: (e: React.MouseEvent) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleFieldToggle(field.id);
                        },
                      } : {})}
                      sx={{
                        pl: isMultiSelectable ? 1.5 : 4,
                        opacity: isFieldAlreadyMapped(field.id) ? 0.5 : 1,
                        ...(isSelected && {
                          bgcolor: 'primary.50',
                          borderLeft: '3px solid',
                          borderLeftColor: 'primary.main',
                        }),
                        ...(droppedFrom && !isSelected && {
                          bgcolor: 'warning.50',
                          borderLeft: '3px solid',
                          borderLeftColor: 'warning.main',
                        }),
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {isMultiSelectable && (
                            <Checkbox
                              checked={isSelected}
                              size="small"
                              sx={{ p: 0, mr: 1 }}
                              tabIndex={-1}
                            />
                          )}
                          <Box>
                            <Typography variant="body2">
                              {field.name}
                              {isFieldAlreadyMapped(field.id) && (
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                  (already mapped)
                                </Typography>
                              )}
                            </Typography>
                            {droppedFrom && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                <WarningAmberIcon sx={{ fontSize: 12, color: 'warning.main' }} />
                                <Typography variant="caption" sx={{ color: 'warning.dark', fontSize: '0.65rem' }}>
                                  Was mapped to Column {Number.parseInt(droppedFrom, 10) + 1}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                        {field.required && !isFieldAlreadyMapped(field.id) && !isSelected && (
                          <Chip
                            label="Required"
                            size="small"
                            color="error"
                            sx={{ ml: 1, height: 18, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    </MenuItem>
                  );
                  return subHeader ? [subHeader, menuItem] : [menuItem];
                }),
              ];
            })
          ) : (
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No fields found
              </Typography>
            </MenuItem>
          )}
        </Select>

    </Box>
  );
};
